import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Wrench, Loader2, Check, X, CheckCircle2, XCircle } from "lucide-react";
import { api, money } from "@/lib/api";
import { NumberPlate } from "@/components/NumberPlate";
import { fmtDate } from "@/lib/dateFormat";

export default function QuoteApprove() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setData(await api(`/public/quotations/${token}`)); }
    catch (e) { setError(e.message || "This quotation link is invalid or has expired."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const decide = async (decision) => {
    setSubmitting(true);
    try {
      const res = await api(`/public/quotations/${token}/decide`, { method: "POST", body: JSON.stringify({ decision }) });
      setResult(res.approval?.status || (decision === "approve" ? "approved" : "declined"));
    } catch (e) { setError(e.message || "Could not record your decision."); }
    finally { setSubmitting(false); }
  };

  const decidedStatus = result || (data?.status === "accepted" ? "approved" : data?.status === "rejected" ? "declined" : null);

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center gap-2.5 bg-midnight px-6 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10"><Wrench className="h-4 w-4 text-brand" /></div>
        <span className="font-display text-[15px] font-bold tracking-widest text-white">PRO GARAGES</span>
      </header>

      <div className="mx-auto max-w-xl px-4 py-10">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Loading quotation…</div>
        ) : error ? (
          <div className="rounded-xl border border-danger/30 bg-danger-bg p-6 text-center text-sm font-semibold text-danger">{error}</div>
        ) : (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="quote-approval-card">
            <p className="text-[11px] font-extrabold tracking-widest text-brand">{data.garage_name || "YOUR GARAGE"}</p>
            <h1 className="mt-1 font-display text-3xl font-bold uppercase text-midnight">Your quotation</h1>
            {data.quotation_number && <p className="text-sm font-bold text-muted-foreground">{data.quotation_number}</p>}
            {data.vehicle?.vrm && <div className="mt-3"><NumberPlate vrm={data.vehicle.vrm} size="sm" /> <span className="ml-2 text-sm text-muted-foreground">{data.vehicle.make} {data.vehicle.model}</span></div>}

            {data.title && (
              <div className="mt-5 rounded-lg bg-secondary p-4">
                <p className="text-base font-bold text-midnight">{data.title}</p>
                {data.assessment_notes && <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">{data.assessment_notes}</p>}
              </div>
            )}

            <div className="mt-4 divide-y divide-border border-t-2 border-midnight">
              {(data.items || []).map((it, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="text-midnight">{it.description}</span>
                  <span className="text-muted-foreground">{it.qty} × {money(it.unit_price)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3">
                <span className="text-sm font-extrabold text-midnight">Total (inc. VAT)</span>
                <span className="font-display text-xl font-bold text-midnight">{money(data.totals?.gross)}</span>
              </div>
            </div>

            {data.important_note && <p className="mt-3 rounded-lg bg-warning-bg px-3 py-2 text-xs text-warning">{data.important_note}</p>}

            {decidedStatus ? (
              <div className={`mt-6 flex items-center gap-2 rounded-lg p-4 ${decidedStatus === "approved" ? "bg-success/10 text-success" : "bg-danger-bg text-danger"}`} data-testid="quote-approval-result">
                {decidedStatus === "approved" ? <CheckCircle2 className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
                <p className="font-bold">You {decidedStatus} this quotation{data.approval?.decided_at ? ` on ${fmtDate(data.approval.decided_at)}` : ""}. Thank you — your garage has been notified.</p>
              </div>
            ) : (
              <>
                <p className="mt-5 text-center text-xs text-muted-foreground">Work only begins once you approve. Please confirm your decision below.</p>
                <div className="mt-3 flex gap-3">
                  <button disabled={submitting} onClick={() => decide("approve")} data-testid="quote-approve-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-success py-3.5 text-sm font-extrabold text-white disabled:opacity-60">
                    <Check className="h-5 w-5" />APPROVE
                  </button>
                  <button disabled={submitting} onClick={() => decide("decline")} data-testid="quote-decline-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-danger py-3.5 text-sm font-extrabold text-danger disabled:opacity-60">
                    <X className="h-5 w-5" />DECLINE
                  </button>
                </div>
              </>
            )}
            <p className="mt-5 text-center text-[11px] text-muted-foreground">This link is unique to you.</p>
          </div>
        )}
      </div>
    </div>
  );
}
